/**
 * Блок «Процессуальное» (ТЗ п. 5.2.11, правила R-06…R-08, R-29).
 *
 * Здесь же ведётся чек-лист фактического наличия документов из перечня
 * приложений (FR-18): незакрытые позиции показываются предупреждением,
 * но генерацию не блокируют.
 */

import { useEffect, useState } from 'react';
import { buildAppendix, computeBranching, newRowId, type AppendixItemEntry } from '@bd/domain';
import { api } from '../../../api.js';
import { DateField, Field, SelectField, TextField } from '../../../components/fields/index.js';
import type { BlockProps } from './types.js';

interface SroEntry {
  id: string;
  name: string;
  fullName?: string;
  fullNameGenitive?: string;
  ogrn?: string;
  inn?: string;
  legalAddress?: string;
  actualAddress?: string;
  postalAddress?: string;
}

export function LegalBlock({ profile, update, readOnly, errorFor }: BlockProps) {
  const legal = profile.legal;
  const [sroList, setSroList] = useState<SroEntry[]>([]);
  const [bureaus, setBureaus] = useState<Array<{ id: string; name: string; reportKinds?: string[] }>>([]);
  const [appendixItems, setAppendixItems] = useState<AppendixItemEntry[]>([]);

  useEffect(() => {
    void api.dictionary('sro').then((r) => setSroList(r.entries as SroEntry[])).catch(() => undefined);
    void api.dictionary('credit-bureaus').then((r) => setBureaus(r.entries as never)).catch(() => undefined);
    void api
      .dictionary('appendix-items')
      .then((r) => setAppendixItems(r.entries as unknown as AppendixItemEntry[]))
      .catch(() => undefined);
  }, []);

  const set = (patch: Partial<typeof legal>) => update((p) => ({ ...p, legal: { ...p.legal, ...patch } }));

  const pickSro = (id: string) => {
    const sro = sroList.find((s) => s.id === id);
    if (!sro) return;
    // Реквизиты копируются в дело: архивирование записи справочника
    // не должно менять ранее сформированные документы.
    set({
      sro: {
        id: sro.id,
        name: sro.name,
        fullName: sro.fullName,
        fullNameGenitive: sro.fullNameGenitive,
        ogrn: sro.ogrn,
        inn: sro.inn,
        legalAddress: sro.legalAddress,
        actualAddress: sro.actualAddress,
        postalAddress: sro.postalAddress,
      },
    });
  };

  const reportKinds = bureaus.find((b) => b.id === legal.creditBureau.id)?.reportKinds ?? [
    'Кредитный отчёт субъекта кредитной истории',
  ];

  // Перечень приложений считается тем же кодом, что и при генерации
  const appendix = appendixItems.length
    ? buildAppendix({ items: appendixItems, profile, flags: computeBranching(profile) })
    : [];

  const toggleCollected = (key: string) =>
    set({ appendixChecklist: { ...legal.appendixChecklist, [key]: !legal.appendixChecklist[key] } });

  return (
    <section className="block">
      <h2>Процессуальное</h2>

      <Field label="СРО арбитражных управляющих" required error={errorFor('legal.sro')}>
        <select value={legal.sro.id ?? ''} disabled={readOnly} onChange={(e) => pickSro(e.target.value)}>
          <option value="">— выберите СРО —</option>
          {sroList.map((sro) => (
            <option key={sro.id} value={sro.id}>
              {sro.name}
            </option>
          ))}
        </select>
      </Field>

      <div className="row row-2">
        <Field label="Бюро кредитных историй" error={errorFor('legal.creditBureau')}>
          <select
            value={legal.creditBureau.id ?? ''}
            disabled={readOnly}
            onChange={(e) => {
              const bureau = bureaus.find((b) => b.id === e.target.value);
              set({ creditBureau: { id: bureau?.id ?? null, name: bureau?.name ?? '' } });
            }}
          >
            <option value="">— выберите бюро —</option>
            {bureaus.map((bureau) => (
              <option key={bureau.id} value={bureau.id}>
                {bureau.name}
              </option>
            ))}
          </select>
        </Field>

        <SelectField
          label="Вид кредитного отчёта"
          value={legal.creditReportKind}
          options={reportKinds}
          error={errorFor('legal.creditReportKind')}
          readOnly={readOnly}
          onChange={(v) => set({ creditReportKind: v })}
        />
      </div>

      <p className="hint">
        Два абзаца заявления в документ не подставляются и правятся вручную в готовом файле: цель расходования
        кредитных средств и формулировка о невозможности представить справки кредиторов. По решению заказчика
        в шаблоне оставлены заполнители из образца.
      </p>

      <DateField
        label="Дата заявления"
        value={legal.applicationDate}
        readOnly={readOnly}
        onChange={(v) => set({ applicationDate: v })}
      />

      <h3>Перечень приложений и чек-лист наличия</h3>
      <p className="hint">
        Перечень формируется автоматически по правилам раздела 6.4 ТЗ. Отметьте документы, которые фактически
        собраны: незакрытые позиции попадут в предупреждения, но генерацию не заблокируют.
      </p>

      <ol className="appendix-list">
        {appendix.map((line) => (
          <li key={line.key}>
            <label className="field field-inline">
              <input
                type="checkbox"
                checked={Boolean(legal.appendixChecklist[line.key])}
                disabled={readOnly}
                onChange={() => toggleCollected(line.key)}
              />
              <span>
                {line.num}. {line.text}
                {line.custom && <span className="hint"> (добавлено вручную)</span>}
              </span>
            </label>
          </li>
        ))}
      </ol>

      <h3>Дополнительные пункты приложений</h3>
      {legal.extraAppendixItems.map((item, index) => (
        <div className="row row-3" key={item.id}>
          <TextField
            label={`Пункт ${index + 1}`}
            value={item.text}
            readOnly={readOnly}
            onChange={(v) =>
              set({ extraAppendixItems: legal.extraAppendixItems.map((x) => (x.id === item.id ? { ...x, text: v } : x)) })
            }
          />
          <TextField
            label="Позиция в перечне"
            value={item.position === null ? '' : String(item.position)}
            hint="Пусто — в конец перечня"
            readOnly={readOnly}
            onChange={(v) =>
              set({
                extraAppendixItems: legal.extraAppendixItems.map((x) =>
                  x.id === item.id ? { ...x, position: v ? Number(v) : null } : x,
                ),
              })
            }
          />
          <button
            type="button"
            className="link-button"
            disabled={readOnly}
            onClick={() => set({ extraAppendixItems: legal.extraAppendixItems.filter((x) => x.id !== item.id) })}
          >
            Удалить
          </button>
        </div>
      ))}

      <button
        type="button"
        className="link-button"
        disabled={readOnly}
        onClick={() => set({ extraAppendixItems: [...legal.extraAppendixItems, { id: newRowId(), text: '', position: null }] })}
      >
        + Добавить пункт
      </button>
    </section>
  );
}
